import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Lenis from 'lenis';
import { useTheme } from '../contexts/ThemeContext';
import './Landing.css';

// A line of text wrapped in an overflow-hidden mask. The inner span starts
// translated down 110% and snaps up to 0 once the parent gets .is-revealed.
// This is the bymonolog/Lenis-era reveal: text "lifts up" through a curtain,
// not a soft fade.
function MaskLine({ children, delay = 0, as: Tag = 'span', className = '' }) {
  return (
    <Tag className={`mask-line ${className}`}>
      <span className="mask-line-inner" style={{ transitionDelay: `${delay}ms` }}>{children}</span>
    </Tag>
  );
}

// Cinematic hero rotation — each frame chosen for cool light, machine geometry,
// and saturation that pairs with the dark UI. Stacked for cross-fade.
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1800&q=85',
  'https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?w=1800&q=85',
  'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1800&q=85',
  'https://images.unsplash.com/photo-1505765050516-f72dcac9c60e?w=1800&q=85',
  'https://images.unsplash.com/photo-1614851099511-773084f6911d?w=1800&q=85',
];

// Q&A copy — kept in sync 1:1 with the FAQPage JSON-LD in index.html so the
// visible page satisfies Google's "answer must be on the page" requirement
// and AI answer engines can quote directly from either surface.
const FAQ = [
  {
    q: 'What is Aegent?',
    a: 'Aegent is an autonomous AI workforce built by akakAI. You define a role — customer support, operations, research, incident response — and Aegent stands up an AI agent that owns it around the clock. Every agent works across the tools your team already uses, thinks through what needs to happen, and takes action, with a second AI reviewing every move so nothing goes out unless it’s right.',
    accent: '#6366f1',
  },
  {
    q: 'How is Aegent different from other AI tools?',
    a: 'Most AI tools respond when you prompt them. Aegent works while you sleep. Every action is reviewed by a second AI before it goes live, so you get real autonomy without giving up oversight. And every interaction sharpens the agent — the one you deploy this week is measurably more capable next quarter.',
    accent: '#ec4899',
  },
  {
    q: 'What can an Aegent agent actually do for my business?',
    a: 'Anything a member of your team could do at a keyboard. Aegent agents answer customers, close tickets, message teammates in Slack, pull data from your systems, run scheduled reports, respond to alerts, and coordinate end-to-end workflows. Common systems are pre-wired; anything custom is a short conversation away.',
    accent: '#38bdf8',
  },
  {
    q: 'How do I roll out an AI agent with Aegent?',
    a: 'Three moves. First, describe the job — the role, the mission, the boundaries. Second, connect it to the channels and tools you already use. Third, turn it on. It’s live in minutes and improving from the very first interaction.',
    accent: '#a855f7',
  },
  {
    q: 'How do I know an Aegent agent won’t do something it shouldn’t?',
    a: 'Every action an Aegent agent takes is reviewed by an independent AI supervisor before it ever leaves the system. If something looks off — unsafe, off-mission, low-confidence — it’s blocked or handed to a human with the full picture. You get speed and scale without giving up oversight.',
    accent: '#f59e0b',
  },
  {
    q: 'Is Aegent free to try?',
    a: 'Yes. Free tier, no credit card. Your first autonomous agent can be live in minutes. Paid plans unlock higher usage and enterprise controls; reach out at sales@akakai.com for enterprise.',
    accent: '#10b981',
  },
];

// Full-bleed scenes that pin as you scroll past them — each is its own
// micro-moment with copy that lands when the image is fully in view.
const SCENES = [
  {
    src: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=3200&q=85&auto=format&fit=crop',
    eyebrow: 'Listens',
    title: 'It notices what needs doing.',
    body: 'New customer emails, incoming messages, scheduled check-ins, alerts — your Aegent stays awake for all of it, so nothing falls through the cracks.',
    align: 'left',
    accent: '#6366f1',
  },
  {
    src: 'https://images.unsplash.com/photo-1466854076813-4aa9ac0fc347?w=3200&q=85&auto=format&fit=crop',
    eyebrow: 'Thinks',
    title: 'It thinks it through.',
    body: 'Before doing anything, your Aegent pulls up past conversations, customer history, and your business rules — then figures out the right move. A second AI double-checks it before anything actually happens.',
    align: 'right',
    accent: '#ec4899',
    darken: true,
  },
  {
    src: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=2000&q=85',
    eyebrow: 'Acts',
    title: 'Then it just does it.',
    body: 'Emails answered. Follow-ups sent. Records updated. Slack pinged. Anything a teammate could do at a computer, your Aegent handles — on its own.',
    align: 'left',
    accent: '#38bdf8',
  },
];

// One full-bleed parallax scene. Image pins, scales as you scroll past it,
// copy slides in from the assigned side. The three scenes together replace
// the old `image-break` one-shot with a real cinematic act.
function ParallaxScene({ scene, idx, container }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    container,
    offset: ['start end', 'end start'],
  });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1.0]);
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const textY = useTransform(scrollYProgress, [0.1, 0.6], [60, 0]);
  const textOpacity = useTransform(scrollYProgress, [0.1, 0.35, 0.7, 0.95], [0, 1, 1, 0]);
  const tintOpacity = useTransform(scrollYProgress, [0.2, 0.5, 0.8], [0, 0.55, 0]);

  return (
    <section className={`scene scene--${scene.align} ${scene.darken ? 'scene--darken' : ''}`} ref={ref}>
      <motion.div className="scene-img-wrap" style={{ scale: imgScale, y: imgY }}>
        <img src={scene.src} alt="" className="scene-img" />
      </motion.div>
      <motion.div
        className="scene-tint"
        style={{ opacity: tintOpacity, background: `radial-gradient(circle at 50% 50%, ${scene.accent}40, transparent 70%)` }}
        aria-hidden="true"
      />
      <div className="scene-vignette" aria-hidden="true" />
      <motion.div className="scene-content" style={{ y: textY, opacity: textOpacity }}>
        <p className="scene-eyebrow" style={{ color: scene.accent }}>
          <span className="scene-eyebrow-mark" style={{ background: scene.accent }} />
          {`0${idx + 1}`} · {scene.eyebrow}
        </p>
        <h2 className="scene-title">
          <MaskLine as="span">{scene.title}</MaskLine>
        </h2>
        <p className="scene-body">
          <MaskLine as="span" delay={120}>{scene.body}</MaskLine>
        </p>
      </motion.div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  // Hero is pinned to the third frame in HERO_IMAGES — no rotation.
  const currentImage = 2;
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ container: containerRef });
  const heroImgScale = useTransform(scrollYProgress, [0, 0.25], [1, 1.18]);
  const heroImgOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.25]);
  const heroContentY = useTransform(scrollYProgress, [0, 0.25], [0, -60]);
      const heroContentOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);

  // Lenis runs the scroll loop — momentum, easing, and a single global rAF
  // tick that drives both the smooth scroll AND framer-motion's useScroll.
  // Without binding it to the inner scroll element, the scenes' parallax would
  // sample the native scroll rather than the eased one and the page would
  // feel like two engines running at once.
  useEffect(() => {
    const wrapper = containerRef.current;
    if (!wrapper) return;
    const lenis = new Lenis({
      wrapper,
      content: wrapper.firstElementChild,
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: true,
      touchMultiplier: 1.4,
    });
    let raf;
    const tick = (time) => { lenis.raf(time); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  // Single observer drives every `.reveal` and `.mask-line` element. We add
  // `.is-revealed` once, then unobserve — no re-trigger when scrolling back up.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const targets = root.querySelectorAll('.reveal, .mask-line');
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-revealed');
          io.unobserve(e.target);
        }
      }),
      { root, threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
    );
    targets.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);


  return (
    <div className="landing">
      <nav className="landing-nav">
        <div className="nav-left">
          <span className="nav-wordmark">AEGENT</span>
        </div>
        <div className="nav-right">
          <button className="nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
          <button className="nav-link" onClick={() => navigate('/auth')}>Sign In</button>
          <button className="nav-btn-cta" onClick={() => navigate('/auth')}>Get Started</button>
          <button className="nav-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {theme === 'dark' ? '○' : '●'}
          </button>
        </div>
      </nav>

      <div className="landing-scroll" ref={containerRef}>
        {/* HERO — cross-faded photography, parallax-zoom, full-color light */}
        <section className="hero">
          <motion.div
            className="hero-image-container"
            style={{ scale: heroImgScale, opacity: heroImgOpacity }}
          >
            {HERO_IMAGES.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className={`hero-image ${i === currentImage ? 'active' : ''}`}
              />
            ))}
            <div className="hero-image-overlay" />
            <div className="hero-image-grain" aria-hidden="true" />
          </motion.div>

          <motion.div className="hero-content" style={{ y: heroContentY, opacity: heroContentOpacity }}>
            <h1 className="hero-title t-display">
              <MaskLine as="span" delay={350} className="is-hero-line">Make Anything</MaskLine>
              <MaskLine as="span" delay={480} className="is-hero-line">Happen.</MaskLine>
            </h1>
            <p className="hero-sub mask-line is-hero-line" style={{ '--hero-line-delay': '720ms' }}>
              <span className="mask-line-inner">
                Aegent automates the busywork of your business — answering emails, chasing follow-ups, running reports — so you can focus on what actually matters.
              </span>
            </p>
            <div className="hero-actions mask-line is-hero-line" style={{ '--hero-line-delay': '880ms' }}>
              <span className="mask-line-inner hero-actions-inner">
                <button className="btn btn-primary btn-lg" onClick={() => navigate('/auth')}>
                  Hire Your First Agent →
                </button>
                <button className="btn btn-lg" onClick={() => navigate('/docs')}>
                  Read the Docs
                </button>
              </span>
            </div>
          </motion.div>

          <motion.div
            className="hero-scroll-indicator"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <span>Scroll</span>
            <div className="scroll-line" />
          </motion.div>
        </section>

        {/* MARQUEE — edge-to-edge keyword ticker */}
        <section className="marquee" aria-hidden="true">
          <div className="marquee-track">
            {Array.from({ length: 4 }).map((_, i) => (
              <span className="marquee-group" key={i}>
                <span>Listens</span><span className="marquee-dot" />
                <span>Thinks</span><span className="marquee-dot" />
                <span>Acts</span><span className="marquee-dot" />
                <span>Learns</span><span className="marquee-dot" />
              </span>
            ))}
          </div>
        </section>

        {/* BOLD DECLARATION */}
        <section className="statement">
          <div className="statement-inner statement-split">
            <div className="statement-headside">
              <p className="statement-number mask-line" style={{ color: '#6366f1' }}><span className="mask-line-inner">01</span></p>
              <h2 className="t-h1 statement-headline">
                <MaskLine as="span">Give it a job.</MaskLine>
                <MaskLine as="span" delay={80}>It gets it done.</MaskLine>
              </h2>
            </div>
            <span className="statement-divider" aria-hidden="true" />
            <div className="statement-bodyside">
              <p className="statement-body mask-line"><span className="mask-line-inner" style={{ transitionDelay: '200ms' }}>
                Tell an Aegent what you need — answer customer emails, chase down late invoices, keep your team's calendar clean, follow up with new leads. It works around the clock, and a second AI double-checks every move before it goes out. You get the results. It handles the how.
              </span></p>
            </div>
          </div>
        </section>

        {/* MANIFESTO — concrete product reality, written in the same key */}
        <section className="statement">
          <div className="statement-inner statement-split">
            <div className="statement-headside">
              <p className="statement-number mask-line" style={{ color: '#ec4899' }}><span className="mask-line-inner">02</span></p>
              <h2 className="t-h1 statement-headline">
                <MaskLine as="span">One for every job.</MaskLine>
                <MaskLine as="span" delay={80}>Sharper every day.</MaskLine>
              </h2>
            </div>
            <span className="statement-divider" aria-hidden="true" />
            <div className="statement-bodyside">
              <p className="statement-body mask-line"><span className="mask-line-inner" style={{ transitionDelay: '200ms' }}>
                Every Aegent focuses on one role — a support agent, a sales follow-up agent, an ops assistant, a research assistant. Each one remembers what happened yesterday, learns from every conversation, and gets measurably better over time. Hire as many as your business needs — and never train them twice.
              </span></p>
            </div>
          </div>
        </section>

        {/* THE THREE ACTS — full-bleed parallax scenes for Listens / Thinks / Acts */}
        {SCENES.map((scene, i) => (
          <ParallaxScene key={i} scene={scene} idx={i} container={containerRef} />
        ))}

        {/* CAPABILITIES */}
        <section className="capabilities">
          <div className="capabilities-header reveal">
            <p className="t-caption">Why Aegent</p>
            <h2 className="t-h2">Built for the real work of running a business</h2>
          </div>
          <div className="capabilities-grid">
            {[
              { num: '01', title: 'Nothing goes out unless it\'s right', desc: 'A second AI double-checks every action before it happens. If something looks off, it stops — so you\'re never cleaning up a mistake.', accent: '#6366f1' },
              { num: '02', title: 'Set it once. Forget about it.', desc: 'Tell your Aegent what job to do, then walk away. No daily prompts, no reminders, no babysitting.', accent: '#ec4899' },
              { num: '03', title: 'Works with the tools you already use', desc: 'Email, Slack, your CRM, spreadsheets, your website — if your team uses it, your Aegent uses it too. The common tools work out of the box.', accent: '#38bdf8' },
              { num: '04', title: 'Gets better every day', desc: 'Every customer, every question, every decision — it remembers. The Aegent you hire this week is measurably sharper next month.', accent: '#a855f7' },
              { num: '05', title: 'Knows when to ask for help', desc: 'When a job actually needs a human, your Aegent hands it off with the full story — never a blank ticket.', accent: '#f59e0b' },
              { num: '06', title: 'Never drops a thread', desc: 'Customers and teammates get the same context every time — even weeks later. Nothing gets lost, nothing gets repeated.', accent: '#10b981' },
            ].map((cap, i) => (
              <div
                key={cap.num}
                className="capability-item reveal"
                data-delay={i % 3}
                style={{ '--cap-accent': cap.accent }}
              >
                <span className="capability-num" style={{ color: cap.accent }}>{cap.num}</span>
                <h3 className="capability-title">{cap.title}</h3>
                <p className="capability-desc">{cap.desc}</p>
                <span className="capability-glow" aria-hidden="true" />
              </div>
            ))}
          </div>
        </section>

        {/* FULL BLEED IMAGE BREAK — dramatic backdrop for the mission/war line */}
        <section className="image-break">
          <img
            src="https://images.unsplash.com/photo-1627495728459-365b5a1f40da?w=3200&q=85&auto=format&fit=crop"
            alt=""
            className="image-break-img"
          />
          <div className="image-break-tint" aria-hidden="true" />
          <div className="image-break-text">
            <p className="t-caption mask-line"><span className="mask-line-inner">Philosophy</span></p>
            <h2 className="t-h1 statement-headline">
              <MaskLine as="span">You define the mission.</MaskLine>
              <MaskLine as="span" delay={80}>The agent wages the war.</MaskLine>
            </h2>
          </div>
        </section>

        {/* PROCESS */}
        <section className="process">
          <div className="process-header reveal">
            <p className="t-caption">How it works</p>
            <h2 className="t-h2">From noticing to doing to learning</h2>
          </div>
          <div className="process-steps">
            {[
              { num: '01', title: 'It notices',              desc: 'A new email lands. A customer signs up. A weekly report comes due. Your Aegent watches for whatever matters, and jumps in the moment it happens.', accent: '#6366f1' },
              { num: '02', title: 'It catches up',           desc: 'It pulls up past conversations, customer records, and your business rules — so it knows exactly what\'s going on before it acts.',              accent: '#ec4899' },
              { num: '03', title: 'It thinks it through',    desc: 'Not a canned reply. Your Aegent actually reasons through what to do next, step by step, like a good employee would.',                          accent: '#38bdf8' },
              { num: '04', title: 'It does the work',        desc: 'Sends the email. Updates the record. Posts to Slack. Fills the form. Whatever the job needs, it gets done.',                                    accent: '#a855f7' },
              { num: '05', title: 'A second AI double-checks', desc: 'Before anything reaches your customers or teammates, a second AI reviews it. If it\'s not right, it doesn\'t go out.',                        accent: '#f59e0b' },
              { num: '06', title: 'It gets smarter',         desc: 'Every job done, every question answered, every customer helped — it all becomes memory. Your Aegent keeps learning, forever.',                accent: '#10b981' },
            ].map(step => (
              <div key={step.num} className="process-step reveal">
                <div className="step-num" style={{ color: step.accent }}>{step.num}</div>
                <div className="step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* GETTING STARTED */}
        <section className="getting-started">
          <div className="gs-header reveal">
            <p className="t-caption">Getting started</p>
            <h2 className="t-h2">Your first Aegent, working in three steps</h2>
          </div>
          <div className="gs-grid">
            {[
              { num: '01', t: 'Describe', d: 'Tell your Aegent the job — the role it\'s covering, what it should and shouldn\'t do. About two minutes.', accent: '#6366f1' },
              { num: '02', t: 'Connect',  d: 'Hook it up to the tools your team already uses — email, Slack, your CRM. Most common ones are one click.',   accent: '#ec4899' },
              { num: '03', t: 'Turn on',  d: 'Flip the switch. It starts working right away — 24/7 — and only gets better from there.',                     accent: '#38bdf8' },
            ].map((s, i) => (
              <div key={s.num} className="gs-step reveal" data-delay={i} style={{ '--gs-accent': s.accent }}>
                <span className="gs-num" style={{ color: s.accent }}>{s.num}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ — visible answers that match the FAQPage JSON-LD in index.html.
            .seo-answer class is targeted by the SpeakableSpecification for voice AEO. */}
        <section className="faq" itemScope itemType="https://schema.org/FAQPage" aria-labelledby="faq-title">
          <div className="faq-header reveal">
            <p className="t-caption">Answers</p>
            <h2 className="t-h2" id="faq-title">Questions we hear the most</h2>
          </div>
          <div className="faq-list">
            {FAQ.map((item, i) => (
              <article
                key={item.q}
                className="faq-item reveal"
                data-delay={i % 3}
                style={{ '--faq-accent': item.accent }}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <span className="faq-num" style={{ color: item.accent }}>{String(i + 1).padStart(2, '0')}</span>
                <h3 className="faq-q" itemProp="name">
                  <MaskLine as="span">{item.q}</MaskLine>
                </h3>
                <div
                  className="faq-a-wrap"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p className="faq-a seo-answer" itemProp="text">
                    <MaskLine as="span" delay={100}>{item.a}</MaskLine>
                  </p>
                </div>
                <span className="faq-glow" aria-hidden="true" />
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="cta">
          <div className="cta-aurora" aria-hidden="true" />
          <div className="cta-inner reveal">
            <p className="t-caption cta-eyebrow">Hire one now</p>
            <h2 className="t-h1">Make anything happen.</h2>
            <p className="cta-sub">Free to start. No credit card. Your first agent, live in minutes — already on the job.</p>
            <div className="cta-actions">
              <button className="btn btn-primary btn-lg magnetic" onClick={() => navigate('/auth')}>
                Hire Your First Agent →
              </button>
              <button className="btn btn-lg magnetic" onClick={() => navigate('/docs')}>
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="landing-footer">
          <div className="footer-left">
            <span className="footer-wordmark">AEGENT</span>
            <span className="footer-by">by akakAI</span>
          </div>
          <div className="footer-links">
            <a href="/docs">Docs</a>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
          </div>
          <div className="footer-right">&copy; 2026</div>
        </footer>
      </div>
    </div>
  );
}
